import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, FormControlLabel, Checkbox, Typography, CircularProgress } from '@mui/material';
import { apiUrl } from '../utils/api';
import { validatePassword } from '../utils/passwordValidator';
import PasswordPolicyChecklist from './PasswordPolicyChecklist';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number | string;
  onSuccess?: () => void;
}

export default function ChangePasswordDialog({ open, onClose, userId, onSuccess }: ChangePasswordDialogProps) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { isValid: isPasswordValid } = validatePassword(newPw);
  const passwordsMatch = newPw === confirmPw;
  const canSubmit =
    currentPw.length > 0 && isPasswordValid && passwordsMatch && !loading;

  const handleSubmit = () => {
    setError('');
    setLoading(true);
    fetch(apiUrl(`/api/users/${userId}/password`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        current_password: currentPw,
        new_password: newPw,
        confirm_password: confirmPw
      })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Password change failed');
        onSuccess?.();
        onClose();
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  const type = show ? 'text' : 'password';

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <TextField
          label="Current Password"
          type={type}
          fullWidth
          margin="dense"
          value={currentPw}
          onChange={e => setCurrentPw(e.target.value)}
        />
        <TextField
          label="New Password"
          type={type}
          fullWidth
          margin="dense"
          value={newPw}
          onChange={e => setNewPw(e.target.value)}
          error={newPw.length > 0 && !isPasswordValid}
        />
        <PasswordPolicyChecklist password={newPw} />
        <TextField
          label="Repeat New Password"
          type={type}
          fullWidth
          margin="dense"
          value={confirmPw}
          onChange={e => setConfirmPw(e.target.value)}
          error={confirmPw.length > 0 && !passwordsMatch}
          helperText={
            confirmPw.length > 0 && !passwordsMatch ? 'Passwords do not match' : ''
          }
        />
        <FormControlLabel
          control={<Checkbox checked={show} onChange={e => setShow(e.target.checked)} />}
          label="Show passwords"
        />
        {error && <Typography color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

